import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiFetch, readApiBody } from "../services/api";
import { ToastContext } from "./ToastContext";
import { AuthContext } from "./AuthContext";

export const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!token) {
      setWishlist([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch("/wishlist", {}, { authToken: token });
      const data = await readApiBody(res);
      const items = res.ok && Array.isArray(data) ? data : [];

      const withImages = await Promise.all(
        items.map(async (item) => {
          if (item.image) return item;
          try {
            const imgRes = await apiFetch(`/images/product/${item.product_id}`);
            const imgData = imgRes.ok ? await imgRes.json() : null;
            return { ...item, image: imgData?.url || null };
          } catch {
            return item;
          }
        }),
      );

      setWishlist(withImages);
    } catch {
      setWishlist([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const isWishlisted = useCallback(
    (productId) =>
      wishlist.some((item) => Number(item.product_id) === Number(productId)),
    [wishlist],
  );

  const addToWishlist = async (product) => {
    const productId = product?.id ?? product;
    const previous = wishlist;
    setWishlist((w) => [
      {
        id: `local-${productId}`,
        product_id: productId,
        name: product?.name,
        slug: product?.slug,
        price: product?.price,
        sale_price: product?.sale_price,
        stock: product?.stock,
        image: product?.image,
      },
      ...w,
    ]);
    try {
      const res = await apiFetch(
        "/wishlist",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product_id: productId }),
        },
        { authToken: token },
      );
      if (!res.ok) throw new Error();
      toast?.("Added to your wishlist ❤️", "success");
    } catch {
      setWishlist(previous);
      toast?.("Unable to update wishlist. Please try again.", "error");
    }
  };

  const removeFromWishlist = async (productId) => {
    const previous = wishlist;
    setWishlist((w) =>
      w.filter((item) => Number(item.product_id) !== Number(productId)),
    );
    try {
      const res = await apiFetch(
        `/wishlist/${productId}`,
        { method: "DELETE" },
        { authToken: token },
      );
      if (!res.ok) throw new Error();
      toast?.("Removed from your wishlist", "success");
    } catch {
      setWishlist(previous);
      toast?.("Unable to update wishlist. Please try again.", "error");
    }
  };

  const toggleWishlist = async (product) => {
    const productId = product?.id ?? product;
    if (isWishlisted(productId)) {
      await removeFromWishlist(productId);
    } else {
      await addToWishlist(product);
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        loading,
        isWishlisted,
        toggleWishlist,
        removeFromWishlist,
        refreshWishlist: fetchWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}
